import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Cpu, Star, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { api, HttpError } from "@/lib/api";

interface ChatModel {
  id: number;
  label: string;
  model_id: string;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
}

/** 답변 모델 관리 — 웹채팅(arkiveai) 모델 셀렉터에 노출할 모델 등록/기본값 지정.
 *
 * 규칙:
 * - 기본값은 항상 1개. 사용자 웹은 항상 기본값 모델이 선택된 상태로 시작하고,
 *   다른 모델을 골라 질문해도 그 1회만 적용된 뒤 기본값으로 복귀한다.
 * - 첫 등록 모델은 자동 기본값. 기본 모델을 끄거나 삭제하면 다음 모델이 승계.
 * - 모델을 하나도 등록하지 않으면 웹채팅에 셀렉터가 표시되지 않고
 *   기존 자동 라우팅(단순질문→경량 모델)이 유지된다.
 */
export function ModelsPage() {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [modelId, setModelId] = useState("");
  const [makeDefault, setMakeDefault] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["chat-models"],
    queryFn: () => api.get<{ items: ChatModel[] }>("/chat-models").then((r) => r.items),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["chat-models"] });
  const onError = (e: unknown) => {
    window.alert(e instanceof HttpError ? e.message : "요청에 실패했습니다.");
  };

  const createM = useMutation({
    mutationFn: () =>
      api.post("/chat-models", {
        label: label.trim(),
        model_id: modelId.trim(),
        is_default: makeDefault,
        is_active: true,
      }),
    onSuccess: () => {
      setLabel("");
      setModelId("");
      setMakeDefault(false);
      invalidate();
    },
    onError,
  });
  const defaultM = useMutation({
    mutationFn: (m: ChatModel) => api.patch(`/chat-models/${m.id}`, { is_default: true }),
    onSuccess: invalidate,
    onError,
  });
  const toggleM = useMutation({
    mutationFn: (m: ChatModel) => api.patch(`/chat-models/${m.id}`, { is_active: !m.is_active }),
    onSuccess: invalidate,
    onError,
  });
  const deleteM = useMutation({
    mutationFn: (id: number) => api.delete(`/chat-models/${id}`),
    onSuccess: invalidate,
    onError,
  });

  const items = data ?? [];
  const activeCount = items.filter((m) => m.is_active).length;

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">모델 관리</h1>
        <p className="text-xs text-muted-foreground">
          사용자 웹(arkiveai) 질문 입력창의 <b>모델 셀렉터</b>에 노출됩니다. 사용자는 질문 1회에
          한해 다른 모델을 고를 수 있고, 전송 후에는 <b>항상 기본값 모델로 복귀</b>합니다.
        </p>
      </header>

      {/* 등록 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cpu className="h-4 w-4" /> 새 모델 등록
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className="text-xs text-muted-foreground">표시명 (사용자에게 보임)</label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="예) Sonnet 5"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">모델 ID (API 식별자)</label>
              <Input
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                placeholder="예) claude-sonnet-5-20260115"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={makeDefault}
                onChange={(e) => setMakeDefault(e.target.checked)}
                className="h-4 w-4"
              />
              기본값으로 지정 (기존 기본값은 자동 해제)
            </label>
            <Button
              className="ml-auto"
              disabled={!label.trim() || !modelId.trim() || createM.isPending}
              onClick={() => createM.mutate()}
            >
              등록
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            ※ 첫 번째로 등록하는 모델은 자동으로 기본값이 됩니다. 모델을 하나도 등록하지 않으면
            셀렉터가 숨겨지고 기존 자동 라우팅(단순 질문 → 경량 모델)이 유지됩니다.
          </p>
        </CardContent>
      </Card>

      {/* 목록 */}
      <Card>
        <CardHeader>
          <CardTitle>등록된 모델 {activeCount > 0 && `(활성 ${activeCount})`}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              등록된 모델이 없습니다. 웹채팅에는 모델 셀렉터가 표시되지 않습니다.
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((m) => (
                <li key={m.id} className="flex items-center gap-3 py-3">
                  <button
                    onClick={() => !m.is_default && m.is_active && defaultM.mutate(m)}
                    disabled={m.is_default || !m.is_active || defaultM.isPending}
                    title={
                      m.is_default
                        ? "현재 기본값"
                        : m.is_active
                          ? "기본값으로 지정"
                          : "비활성 모델은 기본값 지정 불가"
                    }
                    aria-label={m.is_default ? "현재 기본값" : "기본값으로 지정"}
                    className="shrink-0 disabled:cursor-default"
                  >
                    <Star
                      className={
                        "h-5 w-5 " +
                        (m.is_default
                          ? "fill-amber-400 text-amber-400"
                          : "text-muted-foreground/40 hover:text-amber-400")
                      }
                    />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={"text-sm font-medium " + (m.is_active ? "" : "text-muted-foreground line-through")}>
                      {m.label}
                      {m.is_default && (
                        <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">
                          기본값
                        </span>
                      )}
                    </p>
                    <p className="truncate font-mono text-[11px] text-muted-foreground">{m.model_id}</p>
                  </div>
                  <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={m.is_active}
                      onChange={() => toggleM.mutate(m)}
                      className="h-4 w-4"
                    />
                    {m.is_active ? "활성" : "비활성"}
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (window.confirm(`'${m.label}' 모델을 삭제할까요?`)) deleteM.mutate(m.id);
                    }}
                    aria-label="삭제"
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
